import { useEffect, useState, type ReactNode } from "react";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import { Navigate } from "react-router";
import {
  Ban,
  Camera,
  Check,
  ImageIcon,
  Layers3,
  Lightbulb,
  MapPinned,
  Palette,
  PencilLine,
  Plus,
  RefreshCw,
  ScanLine,
  Shapes,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { CenteredLoader, EmptyCard } from "@/components/common";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import {
  buildBrandVisualUniverseImageLookup,
  countExpectedBrandVisualUniverseImages,
  DEFAULT_UNIVERSE_COLORS,
  getBrandUniverseColorSwatchStyle,
  getBrandUniverseThemeStyle,
  getBrandVisualUniverseImage,
  normalizeBrandUniverseColorToken,
  type BrandVisualUniverse,
  type BrandVisualUniverseImageAsset,
  type BrandVisualUniverseImageSection,
} from "@/lib/brand-visual-universe";
import {
  areBrandVisualUniverseDraftsEqual,
  cloneBrandVisualUniverseDraft,
  createBrandVisualUniverseMotifDraft,
  normalizeBrandVisualUniverseDraft,
} from "@/lib/brand-visual-universe-editor";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@runcheetah/ui";

type PhotographyArchetype = BrandVisualUniverse["photographyArchetypes"][number];
type Environment = BrandVisualUniverse["environments"][number];
type Material = BrandVisualUniverse["materials"][number];
type Motif = BrandVisualUniverse["motifs"][number];
type ColorRole = BrandVisualUniverse["colorRoles"][number];
type IconComponent = typeof Camera;
type TextListKey = "sourceLogic" | "compositionRules" | "antiPatterns";
type VisualUniverseEditableSectionKey =
  | "summary"
  | TextListKey
  | "photographyArchetypes"
  | "environments"
  | "materials"
  | "motifs"
  | "colorRoles";

type ObjectEditorField<T extends object> = {
  key: Extract<keyof T, string>;
  label: string;
  kind: "text" | "textarea" | "list" | "select";
  minItems?: number;
  options?: readonly {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  unsetValue?: string;
};

const UNCLASSIFIED_MOTIF_KIND = "__unclassified__";

const EMPTY_PHOTOGRAPHY_ARCHETYPE: PhotographyArchetype = {
  name: "",
  description: "",
  subjects: [],
  settings: [],
  lighting: "",
  framing: "",
  useWhen: "",
};

const EMPTY_ENVIRONMENT: Environment = { name: "", description: "", cues: [] };
const EMPTY_MATERIAL: Material = { name: "", usage: "", sensoryCue: "" };
const EMPTY_COLOR_ROLE: ColorRole = { name: "", colors: [], role: "", usageGuidance: "" };
const replaceAt = <T,>(items: readonly T[], index: number, value: T): T[] =>
  items.map((item, itemIndex) => (itemIndex === index ? value : item));

const removeAt = <T,>(items: readonly T[], index: number): T[] =>
  items.filter((_, itemIndex) => itemIndex !== index);

const formatGeneratedAt = (value: string | null | undefined, intl: IntlShape) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return intl.formatDate(date, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getImageAlt = (intl: IntlShape, name: string) =>
  intl.formatMessage(
    {
      id: "brandVisualUniverse.image.alt",
      defaultMessage: "Generated visual study for {name}",
    },
    { name },
  );

function ArtworkFrame({
  image,
  name,
  intl,
  className,
  priority = false,
}: {
  image: BrandVisualUniverseImageAsset | null;
  name: string;
  intl: IntlShape;
  className?: string;
  priority?: boolean;
}) {
  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-[2rem] border border-white/10 bg-[var(--universe-ink)] shadow-[0_28px_80px_-38px_rgba(0,0,0,0.75)]",
        className,
      )}
    >
      {image ? (
        <img
          src={image.url}
          alt={getImageAlt(intl, name)}
          loading={priority ? "eager" : "lazy"}
          className="h-full min-h-56 w-full object-cover transition duration-700 group-hover:scale-[1.035]"
        />
      ) : (
        <div
          className="flex h-full min-h-56 flex-col justify-between p-5"
          style={{
            background:
              "radial-gradient(circle at 20% 10%, color-mix(in srgb, var(--universe-accent) 30%, transparent), transparent 30%), radial-gradient(circle at 80% 80%, color-mix(in srgb, var(--universe-secondary) 26%, transparent), transparent 36%), linear-gradient(135deg, var(--universe-ink), #20242a)",
          }}
        >
          <div className="flex items-center gap-2 text-white/55">
            <ImageIcon className="h-4 w-4" aria-hidden="true" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em]">
              <FormattedMessage
                id="brandVisualUniverse.imagePending"
                defaultMessage="Image pending"
              />
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2 opacity-45" aria-hidden="true">
            {Array.from({ length: 12 }, (_, index) => (
              <span key={index} className="aspect-square rounded-full border border-white/35" />
            ))}
          </div>
          <p className="max-w-xs text-xs leading-5 text-white/55">
            <FormattedMessage
              id="brandVisualUniverse.imagePendingDescription"
              defaultMessage="Cheetah will attach a generated study after the next refresh."
            />
          </p>
        </div>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/72 via-black/16 to-transparent p-4 opacity-0 transition duration-300 group-hover:opacity-100">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-white/65">
          {name}
        </p>
      </div>
    </div>
  );
}

function HeroImageStack({
  images,
  intl,
}: {
  images: readonly BrandVisualUniverseImageAsset[];
  intl: IntlShape;
}) {
  const heroImages = images.slice(0, 3);

  return (
    <div className="grid min-h-[28rem] gap-3 sm:grid-cols-2">
      <ArtworkFrame
        image={heroImages[0] ?? null}
        name={
          heroImages[0]?.itemName ??
          intl.formatMessage({
            id: "brandVisualUniverse.heroImageFallback",
            defaultMessage: "Hero study",
          })
        }
        intl={intl}
        priority
        className="sm:row-span-2"
      />
      <ArtworkFrame
        image={heroImages[1] ?? null}
        name={
          heroImages[1]?.itemName ??
          intl.formatMessage({
            id: "brandVisualUniverse.materialImageFallback",
            defaultMessage: "Material study",
          })
        }
        intl={intl}
      />
      <ArtworkFrame
        image={heroImages[2] ?? null}
        name={
          heroImages[2]?.itemName ??
          intl.formatMessage({
            id: "brandVisualUniverse.motifImageFallback",
            defaultMessage: "Motif study",
          })
        }
        intl={intl}
      />
    </div>
  );
}

function CreativeSection({
  index,
  title,
  description,
  icon: Icon,
  children,
  className,
  dark = false,
  action,
}: {
  index: string;
  title: ReactNode;
  description: ReactNode;
  icon: IconComponent;
  children: ReactNode;
  className?: string;
  dark?: boolean;
  action?: ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-[2rem] border border-border/70 bg-card/95 p-4 shadow-sm md:p-6",
        className,
      )}
    >
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-4">
          <span
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-deep",
              dark
                ? "bg-[var(--universe-accent)] text-[var(--universe-on-accent)]"
                : "bg-[var(--universe-ink)] text-white",
            )}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p
              className={cn(
                "font-mono text-[10px] font-bold uppercase tracking-[0.18em]",
                dark ? "text-white/45" : "text-muted-foreground",
              )}
            >
              {index}
            </p>
            <h2
              className={cn(
                "mt-2 font-display text-2xl font-bold tracking-[-0.045em] md:text-3xl",
                dark ? "text-white" : "text-foreground",
              )}
            >
              {title}
            </h2>
            <p
              className={cn(
                "mt-2 max-w-2xl text-sm leading-6",
                dark ? "text-white/62" : "text-muted-foreground",
              )}
            >
              {description}
            </p>
          </div>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

function GuidanceDeck({
  eyebrow,
  title,
  description,
  icon: Icon,
  items,
  tabListLabel,
  tone = "light",
  action,
  editingContent,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  description: ReactNode;
  icon: IconComponent;
  items: readonly string[];
  tabListLabel: string;
  tone?: "light" | "danger";
  action?: ReactNode;
  editingContent?: ReactNode;
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!items.length && !editingContent) {
    return null;
  }

  const activeIndex = Math.min(selectedIndex, items.length - 1);
  const activeItem = items[activeIndex] ?? items[0];
  const goToOffset = (offset: number) => {
    setSelectedIndex((current) => (current + offset + items.length) % items.length);
  };

  return (
    <section
      className={cn(
        "overflow-hidden rounded-[2rem] border p-4 shadow-sm md:p-5",
        tone === "danger"
          ? "border-destructive/20 bg-destructive/5"
          : "border-border/70 bg-card/95",
      )}
    >
      <div className="grid gap-5 lg:grid-cols-[0.72fr_1.28fr]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-4">
            <span
              className={cn(
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-deep",
                tone === "danger"
                  ? "bg-destructive text-destructive-foreground"
                  : "bg-[var(--universe-accent)] text-[var(--universe-on-accent)]",
              )}
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {eyebrow}
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold tracking-[-0.045em] text-foreground md:text-3xl">
                {title}
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
            </div>
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>

        {editingContent ? (
          <div className="min-w-0">{editingContent}</div>
        ) : (
          <div className="min-w-0">
            <div className="flex gap-2 overflow-x-auto pb-1" aria-label={tabListLabel}>
              {items.map((item, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={`${item}-${index}`}
                    type="button"
                    onClick={() => setSelectedIndex(index)}
                    aria-pressed={isActive}
                    className={cn(
                      "min-w-12 rounded-full border px-3 py-1.5 font-mono text-[10px] font-bold transition",
                      isActive
                        ? tone === "danger"
                          ? "border-destructive bg-destructive text-destructive-foreground"
                          : "border-[var(--universe-accent)] bg-[var(--universe-accent)] text-[var(--universe-on-accent)]"
                        : "border-border/70 bg-background/70 text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </button>
                );
              })}
            </div>

            <div className="relative mt-4 overflow-hidden rounded-[1.6rem] border border-border/70 bg-background/75 p-5">
              <div
                className="absolute inset-y-0 left-0 w-1 bg-[var(--universe-accent)]"
                aria-hidden="true"
              />
              <p className="text-base leading-7 text-foreground md:text-lg" aria-live="polite">
                {activeItem}
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  <FormattedMessage
                    id="brandVisualUniverse.deck.position"
                    defaultMessage="{current} of {total}"
                    values={{ current: activeIndex + 1, total: items.length }}
                  />
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => goToOffset(-1)}
                    className="rounded-full border border-border/70 bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:text-foreground"
                  >
                    <FormattedMessage
                      id="brandVisualUniverse.deck.previous"
                      defaultMessage="Previous"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => goToOffset(1)}
                    className="rounded-full bg-[var(--universe-ink)] px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
                  >
                    <FormattedMessage id="brandVisualUniverse.deck.next" defaultMessage="Next" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function PhotographyArchetypeCard({
  archetype,
  intl,
}: {
  archetype: PhotographyArchetype;
  intl: IntlShape;
}) {
  return (
    <article className="relative overflow-hidden rounded-[1.6rem] border border-border/70 bg-background/70 p-5 shadow-sm">
      <span
        className="absolute inset-x-5 top-0 h-1 rounded-b-full bg-[var(--universe-accent)]"
        aria-hidden="true"
      />
      <p className="font-display text-xl font-bold tracking-[-0.04em] text-foreground">
        {archetype.name}
      </p>
      <p className="mt-3 text-sm leading-6 text-muted-foreground">{archetype.description}</p>
      {archetype.framing ? (
        <div className="mt-4 border-t border-border/60 pt-4">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            {intl.formatMessage({
              id: "brandVisualUniverse.editor.field.framing",
              defaultMessage: "Framing",
            })}
          </p>
          <p className="mt-1.5 text-sm leading-6 text-foreground/82">{archetype.framing}</p>
        </div>
      ) : null}
    </article>
  );
}

function ImageStudyCard({
  name,
  description,
  image,
  intl,
  badge,
}: {
  name: string;
  description: string;
  image: BrandVisualUniverseImageAsset | null;
  intl: IntlShape;
  badge?: ReactNode;
}) {
  return (
    <article className="overflow-hidden rounded-[1.8rem] border border-border/70 bg-background/70 shadow-sm">
      <ArtworkFrame
        image={image}
        name={name}
        intl={intl}
        className="aspect-[4/3] rounded-none border-0 shadow-none"
      />
      <div className="p-5">
        {badge ? <div className="mb-3">{badge}</div> : null}
        <p className="font-display text-xl font-bold tracking-[-0.04em] text-foreground">{name}</p>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
    </article>
  );
}

function EnvironmentCard({
  environment,
  image,
  intl,
}: {
  environment: Environment;
  image: BrandVisualUniverseImageAsset | null;
  intl: IntlShape;
}) {
  return (
    <ImageStudyCard
      name={environment.name}
      description={environment.description}
      image={image}
      intl={intl}
    />
  );
}

function MaterialCard({
  material,
  image,
  intl,
}: {
  material: Material;
  image: BrandVisualUniverseImageAsset | null;
  intl: IntlShape;
}) {
  return (
    <ImageStudyCard name={material.name} description={material.usage} image={image} intl={intl} />
  );
}

function MotifCard({
  motif,
  image,
  intl,
}: {
  motif: Motif;
  image: BrandVisualUniverseImageAsset | null;
  intl: IntlShape;
}) {
  const kindLabel =
    motif.kind === "look"
      ? intl.formatMessage({
          id: "brandVisualUniverse.motifKind.look",
          defaultMessage: "Look",
        })
      : motif.kind === "content"
        ? intl.formatMessage({
            id: "brandVisualUniverse.motifKind.content",
            defaultMessage: "Content",
          })
        : intl.formatMessage({
            id: "brandVisualUniverse.motifKind.unclassified",
            defaultMessage: "Unclassified — not used for generation",
          });

  return (
    <ImageStudyCard
      name={motif.name}
      description={motif.usage}
      image={image}
      intl={intl}
      badge={
        <Badge
          shape="pill"
          variant={motif.kind === "look" ? "secondary" : "outline"}
          className={cn(
            "px-2.5 py-1 text-[10px] uppercase tracking-[0.12em]",
            motif.kind ? null : "border-dashed text-muted-foreground",
          )}
        >
          {kindLabel}
        </Badge>
      }
    />
  );
}

function ColorRoleCard({ role, intl }: { role: ColorRole; intl: IntlShape }) {
  const colors = role.colors
    .map(normalizeBrandUniverseColorToken)
    .filter((color): color is string => Boolean(color));
  const swatches = colors.length ? colors : [DEFAULT_UNIVERSE_COLORS[0]];

  return (
    <article className="overflow-hidden rounded-[1.8rem] border border-border/70 bg-background/70 shadow-sm">
      <div
        className="grid min-h-32 overflow-hidden border-b border-border/70"
        style={{ gridTemplateColumns: `repeat(${swatches.length}, minmax(0, 1fr))` }}
        aria-hidden="true"
      >
        {swatches.map((color, index) => (
          <span key={`${color}-${index}`} style={{ backgroundColor: color }} />
        ))}
      </div>
      <div className="space-y-4 p-5">
        <div>
          <p className="font-display text-xl font-bold tracking-[-0.04em] text-foreground">
            {role.name}
          </p>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{role.role}</p>
        </div>
        {colors.length ? (
          <div className="flex flex-wrap gap-2">
            {colors.map((color) => (
              <span
                key={color}
                className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1 font-mono text-xs text-muted-foreground"
              >
                <span
                  className="h-3 w-3 rounded-full border border-border/80"
                  style={getBrandUniverseColorSwatchStyle(color)}
                  aria-hidden="true"
                />
                {color}
              </span>
            ))}
          </div>
        ) : null}
        <div>
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            {intl.formatMessage({
              id: "brandVisualUniverse.colorRoles.usageGuidance",
              defaultMessage: "Usage guidance",
            })}
          </p>
          <p className="mt-1.5 text-sm leading-6 text-foreground/82">{role.usageGuidance}</p>
        </div>
      </div>
    </article>
  );
}

function EditorFieldShell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-2">
      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function TextListEditor({
  title,
  items,
  onChange,
  addLabel,
  removeLabel,
  minItems = 1,
}: {
  title: string;
  items: readonly string[];
  onChange: (items: string[]) => void;
  addLabel: string;
  removeLabel: string;
  minItems?: number;
}) {
  const safeItems = items.length ? [...items] : minItems > 0 ? [""] : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          {title}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => onChange([...safeItems, ""])}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {addLabel}
        </Button>
      </div>
      <div className="space-y-2">
        {safeItems.map((item, index) => (
          <div key={index} className="flex gap-2">
            <Textarea
              value={item}
              onChange={(event) => onChange(replaceAt(safeItems, index, event.target.value))}
              className="min-h-20 rounded-2xl bg-background/70"
              aria-label={`${title} ${index + 1}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mt-1 shrink-0 rounded-full text-muted-foreground hover:text-destructive"
              disabled={safeItems.length <= minItems}
              onClick={() => onChange(removeAt(safeItems, index))}
              aria-label={`${removeLabel} ${title} ${index + 1}`}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ObjectListEditor<T extends object>({
  title,
  items,
  fields,
  createItem,
  onChange,
  addLabel,
  removeLabel,
}: {
  title: string;
  items: readonly T[];
  fields: readonly ObjectEditorField<T>[];
  createItem: () => T;
  onChange: (items: T[]) => void;
  addLabel: string;
  removeLabel: string;
}) {
  const updateItem = (index: number, item: T) => onChange(replaceAt(items, index, item));

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="font-display text-xl font-bold tracking-[-0.04em] text-foreground">
          {title}
        </h3>
        <Button
          type="button"
          variant="outline"
          className="rounded-full"
          onClick={() => onChange([...items, createItem()])}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {addLabel}
        </Button>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {items.map((item, index) => (
          <article key={index} className="space-y-4 rounded-[1.5rem] bg-background/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {String(index + 1).padStart(2, "0")}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rounded-full text-muted-foreground hover:text-destructive"
                disabled={items.length <= 1}
                onClick={() => onChange(removeAt(items, index))}
                aria-label={`${removeLabel} ${title} ${index + 1}`}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            {fields.map((field) => {
              const value = item[field.key];
              if (field.kind === "list") {
                return (
                  <TextListEditor
                    key={field.key}
                    title={field.label}
                    items={Array.isArray(value) ? value : []}
                    minItems={field.minItems ?? 0}
                    addLabel={addLabel}
                    removeLabel={removeLabel}
                    onChange={(nextItems) => updateItem(index, { ...item, [field.key]: nextItems })}
                  />
                );
              }

              if (field.kind === "select") {
                const selectValue =
                  typeof value === "string" && value.length > 0 ? value : (field.unsetValue ?? "");

                return (
                  <EditorFieldShell key={field.key} label={field.label}>
                    <Select
                      value={selectValue}
                      onValueChange={(nextValue) =>
                        updateItem(index, { ...item, [field.key]: nextValue })
                      }
                    >
                      <SelectTrigger className="w-full rounded-full bg-background/70">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </EditorFieldShell>
                );
              }

              const textValue = typeof value === "string" ? value : "";
              return (
                <EditorFieldShell key={field.key} label={field.label}>
                  {field.kind === "textarea" ? (
                    <Textarea
                      value={textValue}
                      onChange={(event) =>
                        updateItem(index, { ...item, [field.key]: event.target.value })
                      }
                      className="rounded-2xl bg-background/70"
                    />
                  ) : (
                    <Input
                      value={textValue}
                      onChange={(event) =>
                        updateItem(index, { ...item, [field.key]: event.target.value })
                      }
                      className="rounded-full bg-background/70"
                    />
                  )}
                </EditorFieldShell>
              );
            })}
          </article>
        ))}
      </div>
    </section>
  );
}

function VisualUniverseSectionEditor({
  section,
  draft,
  onDraftChange,
  onCancel,
  onSave,
  isDirty,
  intl,
}: {
  section: VisualUniverseEditableSectionKey;
  draft: BrandVisualUniverse;
  onDraftChange: (draft: BrandVisualUniverse) => void;
  onCancel: () => void;
  onSave: () => void;
  isDirty: boolean;
  intl: IntlShape;
}) {
  const setTextList = (key: TextListKey, items: string[]) =>
    onDraftChange({ ...draft, [key]: items });
  const addLabel = intl.formatMessage({ id: "common.add", defaultMessage: "Add" });
  const addItemLabel = intl.formatMessage({
    id: "brandVisualUniverse.editor.addItem",
    defaultMessage: "Add item",
  });
  const removeLabel = intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" });
  const labels = {
    colors: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.colors",
      defaultMessage: "Colors",
    }),
    cues: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.cues",
      defaultMessage: "Cues",
    }),
    description: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.description",
      defaultMessage: "Description",
    }),
    framing: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.framing",
      defaultMessage: "Framing",
    }),
    lighting: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.lighting",
      defaultMessage: "Lighting",
    }),
    meaning: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.meaning",
      defaultMessage: "Meaning",
    }),
    motifKind: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.motifKind",
      defaultMessage: "Motif type",
    }),
    name: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.name",
      defaultMessage: "Name",
    }),
    role: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.role",
      defaultMessage: "Role",
    }),
    sensoryCue: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.sensoryCue",
      defaultMessage: "Sensory cue",
    }),
    settings: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.settings",
      defaultMessage: "Settings",
    }),
    subjects: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.subjects",
      defaultMessage: "Subjects",
    }),
    usage: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.usage",
      defaultMessage: "Usage",
    }),
    usageGuidance: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.usageGuidance",
      defaultMessage: "Usage guidance",
    }),
    useWhen: intl.formatMessage({
      id: "brandVisualUniverse.editor.field.useWhen",
      defaultMessage: "Use when",
    }),
  };

  const editorContent = (() => {
    switch (section) {
      case "summary":
        return (
          <EditorFieldShell
            label={intl.formatMessage({
              id: "brandVisualUniverse.editor.summary",
              defaultMessage: "Summary",
            })}
          >
            <Textarea
              value={draft.summary}
              onChange={(event) => onDraftChange({ ...draft, summary: event.target.value })}
              className="min-h-40 rounded-[1.4rem] bg-background/70 text-base leading-7"
            />
          </EditorFieldShell>
        );
      case "sourceLogic":
        return (
          <TextListEditor
            title={intl.formatMessage({
              id: "brandVisualUniverse.editor.sourceLogic",
              defaultMessage: "Source logic",
            })}
            items={draft.sourceLogic}
            addLabel={addLabel}
            removeLabel={removeLabel}
            onChange={(items) => setTextList("sourceLogic", items)}
          />
        );
      case "photographyArchetypes":
        return (
          <ObjectListEditor
            title={intl.formatMessage({
              id: "brandVisualUniverse.editor.photography",
              defaultMessage: "Photography archetypes",
            })}
            items={draft.photographyArchetypes}
            createItem={() => ({ ...EMPTY_PHOTOGRAPHY_ARCHETYPE, subjects: [], settings: [] })}
            onChange={(items) => onDraftChange({ ...draft, photographyArchetypes: items })}
            addLabel={addItemLabel}
            removeLabel={removeLabel}
            fields={[
              { key: "name", label: labels.name, kind: "text" },
              { key: "description", label: labels.description, kind: "textarea" },
              { key: "subjects", label: labels.subjects, kind: "list" },
              { key: "settings", label: labels.settings, kind: "list" },
              { key: "lighting", label: labels.lighting, kind: "textarea" },
              { key: "framing", label: labels.framing, kind: "textarea" },
              { key: "useWhen", label: labels.useWhen, kind: "textarea" },
            ]}
          />
        );
      case "environments":
        return (
          <ObjectListEditor
            title={intl.formatMessage({
              id: "brandVisualUniverse.editor.environments",
              defaultMessage: "Environments",
            })}
            items={draft.environments}
            createItem={() => ({ ...EMPTY_ENVIRONMENT, cues: [] })}
            onChange={(items) => onDraftChange({ ...draft, environments: items })}
            addLabel={addLabel}
            removeLabel={removeLabel}
            fields={[
              { key: "name", label: labels.name, kind: "text" },
              { key: "description", label: labels.description, kind: "textarea" },
              { key: "cues", label: labels.cues, kind: "list" },
            ]}
          />
        );
      case "materials":
        return (
          <ObjectListEditor
            title={intl.formatMessage({
              id: "brandVisualUniverse.editor.materials",
              defaultMessage: "Materials",
            })}
            items={draft.materials}
            createItem={() => ({ ...EMPTY_MATERIAL })}
            onChange={(items) => onDraftChange({ ...draft, materials: items })}
            addLabel={addLabel}
            removeLabel={removeLabel}
            fields={[
              { key: "name", label: labels.name, kind: "text" },
              { key: "usage", label: labels.usage, kind: "textarea" },
              { key: "sensoryCue", label: labels.sensoryCue, kind: "textarea" },
            ]}
          />
        );
      case "motifs":
        return (
          <ObjectListEditor
            title={intl.formatMessage({
              id: "brandVisualUniverse.editor.motifs",
              defaultMessage: "Motifs",
            })}
            items={draft.motifs}
            createItem={createBrandVisualUniverseMotifDraft}
            onChange={(items) => onDraftChange({ ...draft, motifs: items })}
            addLabel={addLabel}
            removeLabel={removeLabel}
            fields={[
              {
                key: "kind",
                label: labels.motifKind,
                kind: "select",
                unsetValue: UNCLASSIFIED_MOTIF_KIND,
                options: [
                  {
                    value: UNCLASSIFIED_MOTIF_KIND,
                    label: intl.formatMessage({
                      id: "brandVisualUniverse.motifKind.unclassified",
                      defaultMessage: "Unclassified — not used for generation",
                    }),
                    disabled: true,
                  },
                  {
                    value: "look",
                    label: intl.formatMessage({
                      id: "brandVisualUniverse.motifKind.look",
                      defaultMessage: "Look",
                    }),
                  },
                  {
                    value: "content",
                    label: intl.formatMessage({
                      id: "brandVisualUniverse.motifKind.content",
                      defaultMessage: "Content",
                    }),
                  },
                ],
              },
              { key: "name", label: labels.name, kind: "text" },
              { key: "meaning", label: labels.meaning, kind: "textarea" },
              { key: "usage", label: labels.usage, kind: "textarea" },
            ]}
          />
        );
      case "colorRoles":
        return (
          <ObjectListEditor
            title={intl.formatMessage({
              id: "brandVisualUniverse.editor.colorRoles",
              defaultMessage: "Color roles",
            })}
            items={draft.colorRoles}
            createItem={() => ({ ...EMPTY_COLOR_ROLE, colors: [] })}
            onChange={(items) => onDraftChange({ ...draft, colorRoles: items })}
            addLabel={addLabel}
            removeLabel={removeLabel}
            fields={[
              { key: "name", label: labels.name, kind: "text" },
              { key: "colors", label: labels.colors, kind: "list" },
              { key: "role", label: labels.role, kind: "textarea" },
              { key: "usageGuidance", label: labels.usageGuidance, kind: "textarea" },
            ]}
          />
        );
      case "compositionRules":
        return (
          <TextListEditor
            title={intl.formatMessage({
              id: "brandVisualUniverse.editor.compositionRules",
              defaultMessage: "Composition rules",
            })}
            items={draft.compositionRules}
            addLabel={addLabel}
            removeLabel={removeLabel}
            onChange={(items) => setTextList("compositionRules", items)}
          />
        );
      case "antiPatterns":
        return (
          <TextListEditor
            title={intl.formatMessage({
              id: "brandVisualUniverse.editor.antiPatterns",
              defaultMessage: "Anti-patterns",
            })}
            items={draft.antiPatterns}
            addLabel={addLabel}
            removeLabel={removeLabel}
            onChange={(items) => setTextList("antiPatterns", items)}
          />
        );
    }
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" className="rounded-full" onClick={onCancel}>
          <X className="h-4 w-4" aria-hidden="true" />
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          type="button"
          variant="brand"
          className="rounded-full"
          onClick={onSave}
          disabled={!isDirty}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
          <FormattedMessage
            id="brandVisualUniverse.editor.updateSection"
            defaultMessage="Update section"
          />
        </Button>
      </div>

      {editorContent}
    </div>
  );
}

export default function BrandVisualUniversePage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();
  const utils = trpc.useUtils();
  const [generationQueued, setGenerationQueued] = useState(false);
  const query = trpc.brand.getVisualUniverse.useQuery(
    { brandId: brand?.id ?? "" },
    {
      enabled: Boolean(brand?.id),
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: (visualUniverseQuery) => {
        const generationState = visualUniverseQuery.state.data?.generationStatus.state;
        return generationQueued || generationState === "queued" || generationState === "processing"
          ? 5_000
          : false;
      },
    },
  );
  const visualStyleQuery = trpc.brand.getVisualStyle.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 60_000 },
  );
  const generateMutation = trpc.brand.generateVisualUniverse.useMutation({
    onSuccess: async () => {
      if (!brand?.id) {
        return;
      }

      setGenerationQueued(true);
      await utils.brand.getVisualUniverse.invalidate({ brandId: brand.id });
    },
  });
  const applyChangesMutation = trpc.brand.updateVisualUniverse.useMutation();
  const [editingSection, setEditingSection] = useState<VisualUniverseEditableSectionKey | null>(
    null,
  );
  const [draftUniverse, setDraftUniverse] = useState<BrandVisualUniverse | null>(null);
  const [draftBaselineUniverse, setDraftBaselineUniverse] = useState<BrandVisualUniverse | null>(
    null,
  );
  const [isDraftDirty, setIsDraftDirty] = useState(false);
  const [pendingVisualUniverse, setPendingVisualUniverse] = useState<BrandVisualUniverse | null>(
    null,
  );
  const persistedUniverse = query.data?.visualUniverse ?? null;
  const universe = pendingVisualUniverse ?? persistedUniverse;
  const hasPendingImageRegenerationChanges = pendingVisualUniverse !== null;
  const images = query.data?.images ?? [];
  const imageLookup = buildBrandVisualUniverseImageLookup(images);
  const generationState = query.data?.generationStatus.state ?? "idle";
  const generatedAtLabel = formatGeneratedAt(query.data?.generatedAt, intl);
  const isGenerationPending = generationState === "queued" || generationState === "processing";
  const isGenerating = generationQueued || isGenerationPending || generateMutation.isPending;
  const isEditing = editingSection !== null;
  const applyChangesDisabled =
    !brand?.id ||
    !universe ||
    !hasPendingImageRegenerationChanges ||
    isEditing ||
    isGenerating ||
    applyChangesMutation.isPending;
  const expectedImageCount = universe ? countExpectedBrandVisualUniverseImages(universe) : 0;
  const imageCoverageLabel = intl.formatMessage(
    {
      id: "brandVisualUniverse.imagesCoverage",
      defaultMessage: "{ready} of {total} studies ready",
    },
    { ready: images.length, total: expectedImageCount },
  );
  const guidanceItemsLabel = intl.formatMessage({
    id: "brandVisualUniverse.deck.itemsAria",
    defaultMessage: "Visual universe guidance items",
  });
  const brandPaletteColors = [
    ...(visualStyleQuery.data?.colorPalette ?? []),
    ...(visualStyleQuery.data?.secondaryColorPalette ?? []),
  ];
  const displayedBrandColors = Array.from(
    new Set(
      brandPaletteColors
        .map(normalizeBrandUniverseColorToken)
        .filter((color): color is string => Boolean(color)),
    ),
  ).slice(0, 8);
  const brandThemeStyle = getBrandUniverseThemeStyle(brandPaletteColors);

  const findImage = (
    section: BrandVisualUniverseImageSection,
    itemName: string,
    itemIndex: number,
  ) => getBrandVisualUniverseImage(imageLookup, { section, itemName, itemIndex });

  const handleGenerate = () => {
    if (brand?.id) {
      generateMutation.mutate({ brandId: brand.id });
    }
  };

  const setCleanDraftUniverse = (visualUniverse: BrandVisualUniverse | null) => {
    setDraftUniverse(visualUniverse ? cloneBrandVisualUniverseDraft(visualUniverse) : null);
    setDraftBaselineUniverse(visualUniverse ? cloneBrandVisualUniverseDraft(visualUniverse) : null);
    setIsDraftDirty(false);
  };

  const handleDraftChange = (draft: BrandVisualUniverse) => {
    setDraftUniverse(draft);
    setIsDraftDirty(
      Boolean(
        draftBaselineUniverse && !areBrandVisualUniverseDraftsEqual(draft, draftBaselineUniverse),
      ),
    );
  };

  const handleStartEditing = (section: VisualUniverseEditableSectionKey) => {
    if (!universe) {
      return;
    }

    setCleanDraftUniverse(universe);
    setEditingSection(section);
  };

  const handleCancelEditing = () => {
    setCleanDraftUniverse(universe);
    setEditingSection(null);
  };

  const handleSaveEditing = () => {
    if (!draftUniverse) {
      return;
    }

    let visualUniverse: BrandVisualUniverse;
    try {
      visualUniverse = normalizeBrandVisualUniverseDraft(draftUniverse);
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandVisualUniverse.editor.validationError",
          defaultMessage: "Fill every required visual-universe field before saving.",
        }),
      );
      return;
    }

    setPendingVisualUniverse(visualUniverse);
    setCleanDraftUniverse(visualUniverse);
    setEditingSection(null);
    toast.success(
      intl.formatMessage({
        id: "brandVisualUniverse.editor.saveSuccess",
        defaultMessage: "Section updated. Apply changes to save and regenerate studies.",
      }),
    );
  };

  const handleApplyChanges = async () => {
    if (!brand?.id || !pendingVisualUniverse) {
      return;
    }

    try {
      const result = await applyChangesMutation.mutateAsync({
        brandId: brand.id,
        visualUniverse: pendingVisualUniverse,
        queueImageRegeneration: true,
      });
      utils.brand.getVisualUniverse.setData({ brandId: brand.id }, result);
      setCleanDraftUniverse(result.visualUniverse ?? null);
      setPendingVisualUniverse(null);
      setGenerationQueued(true);
      toast.success(
        intl.formatMessage({
          id: "brandVisualUniverse.applyChangesSuccess",
          defaultMessage: "Changes applied. Fresh image studies are queued.",
        }),
      );
      await utils.brand.getVisualUniverse.invalidate({ brandId: brand.id });
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandVisualUniverse.applyChangesError",
          defaultMessage: "Could not apply changes",
        }),
      );
    }
  };

  useEffect(() => {
    if (!query.isFetching && (persistedUniverse || generationState === "idle")) {
      setGenerationQueued(false);
    }
  }, [generationState, persistedUniverse, query.isFetching]);

  useEffect(() => {
    if (!persistedUniverse) {
      setCleanDraftUniverse(null);
      setEditingSection(null);
      setPendingVisualUniverse(null);
      return;
    }

    if (!isEditing && !pendingVisualUniverse) {
      setCleanDraftUniverse(persistedUniverse);
    }
  }, [isEditing, pendingVisualUniverse, persistedUniverse]);

  const renderEditAction = (section: VisualUniverseEditableSectionKey, dark = false) => {
    const isCurrentSection = editingSection === section;
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => handleStartEditing(section)}
        disabled={isEditing}
        className={cn(
          "rounded-full",
          dark
            ? "border-white/15 bg-white/8 text-white hover:bg-white/14 hover:text-white"
            : "bg-background/70",
        )}
      >
        <PencilLine className="h-4 w-4" aria-hidden="true" />
        {isCurrentSection ? (
          <FormattedMessage
            id="brandVisualUniverse.editor.editingSection"
            defaultMessage="Editing"
          />
        ) : (
          <FormattedMessage id="brandVisualUniverse.editor.editSection" defaultMessage="Edit" />
        )}
      </Button>
    );
  };

  const renderSectionEditor = (section: VisualUniverseEditableSectionKey) =>
    editingSection === section && draftUniverse ? (
      <VisualUniverseSectionEditor
        section={section}
        draft={draftUniverse}
        onDraftChange={handleDraftChange}
        onCancel={handleCancelEditing}
        onSave={handleSaveEditing}
        isDirty={isDraftDirty}
        intl={intl}
      />
    ) : null;

  const isSectionEditing = (section: VisualUniverseEditableSectionKey) =>
    editingSection === section;

  if (brandQuery.isPending || query.isPending) {
    return <CenteredLoader />;
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  return (
    <div
      className="mx-auto w-full max-w-7xl space-y-6 px-4 pb-32 pt-5 sm:px-6 lg:px-8"
      style={brandThemeStyle}
    >
      {!universe ? (
        <EmptyCard
          className="border-border/70 bg-card/90 shadow-sm"
          icon={<Sparkles className="h-6 w-6" aria-hidden="true" />}
        >
          <div className="max-w-xl space-y-4">
            <p className="font-display text-2xl font-bold tracking-[-0.04em] text-foreground">
              {isGenerating ? (
                <FormattedMessage
                  id="brandVisualUniverse.generatingTitle"
                  defaultMessage="Visual universe is being generated"
                />
              ) : (
                <FormattedMessage
                  id="brandVisualUniverse.emptyTitle"
                  defaultMessage="Generate your visual universe"
                />
              )}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              {isGenerating ? (
                <FormattedMessage
                  id="brandVisualUniverse.generatingDescription"
                  defaultMessage="Cheetah is still building the visual universe from your brand profile and source material. This page will update automatically when it is ready."
                />
              ) : (
                <FormattedMessage
                  id="brandVisualUniverse.emptyDescription"
                  defaultMessage="No visual universe is available yet. Generate one from the current brand profile and source material."
                />
              )}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button type="button" onClick={handleGenerate} disabled={isGenerating || !brand?.id}>
                {isGenerating ? (
                  <FormattedMessage
                    id="brandVisualUniverse.generatePendingButton"
                    defaultMessage="Generating..."
                  />
                ) : (
                  <FormattedMessage
                    id="brandVisualUniverse.generateButton"
                    defaultMessage="Generate visual universe"
                  />
                )}
              </Button>
              {generateMutation.error ? (
                <p className="text-sm text-destructive">
                  <FormattedMessage
                    id="brandVisualUniverse.generateError"
                    defaultMessage="Could not queue generation. Try again."
                  />
                </p>
              ) : null}
            </div>
          </div>
        </EmptyCard>
      ) : (
        <>
          <Card className="relative overflow-hidden rounded-[2.5rem] border-0 bg-[var(--universe-ink)] text-white shadow-[0_34px_90px_-45px_rgba(0,0,0,0.9)]">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 12% 0%, color-mix(in srgb, var(--universe-accent) 38%, transparent), transparent 32%), radial-gradient(circle at 90% 22%, color-mix(in srgb, var(--universe-secondary) 28%, transparent), transparent 24%), radial-gradient(circle at 76% 88%, color-mix(in srgb, var(--universe-tertiary) 20%, transparent), transparent 28%), linear-gradient(120deg, rgba(255,255,255,0.09), transparent 42%)",
              }}
              aria-hidden="true"
            />
            <CardContent className="relative grid gap-8 p-5 md:p-8 lg:grid-cols-[0.9fr_1.1fr] lg:p-10">
              <div className="flex flex-col justify-between gap-8">
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="rounded-full bg-[var(--universe-accent)] px-3 py-1 text-[var(--universe-on-accent)] hover:bg-[var(--universe-accent)]">
                      <FormattedMessage
                        id="brandVisualUniverse.hero.eyebrow"
                        defaultMessage="Creative direction board"
                      />
                    </Badge>
                    {generatedAtLabel ? (
                      <Badge
                        variant="outline"
                        className="rounded-full border-white/15 bg-white/8 px-3 py-1 text-white/70"
                      >
                        {intl.formatMessage(
                          {
                            id: "brandVisualUniverse.generatedAtValue",
                            defaultMessage: "Generated {date}",
                          },
                          { date: generatedAtLabel },
                        )}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="space-y-4">
                    <h1 className="max-w-3xl text-balance font-display text-[clamp(2.7rem,7vw,6.8rem)] font-black leading-[0.84] tracking-[-0.08em]">
                      <FormattedMessage
                        id="brandVisualUniverse.title"
                        defaultMessage="Visual Universe"
                      />
                    </h1>
                    {isSectionEditing("summary") ? (
                      <div className="max-w-3xl">{renderSectionEditor("summary")}</div>
                    ) : (
                      <p className="max-w-2xl text-balance text-xl leading-8 text-white/74 md:text-2xl">
                        {universe.summary}
                      </p>
                    )}
                  </div>
                </div>

                {displayedBrandColors.length ? (
                  <div className="space-y-3">
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">
                      <FormattedMessage
                        id="brandVisualUniverse.hero.paletteLabel"
                        defaultMessage="Brand colors in use"
                      />
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {displayedBrandColors.map((color) => (
                        <span
                          key={color}
                          className="h-9 min-w-16 rounded-full border border-white/18 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]"
                          style={{ backgroundColor: color }}
                          title={color}
                          aria-label={color}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    type="button"
                    variant="brand"
                    onClick={handleGenerate}
                    disabled={isGenerating || !brand?.id || hasPendingImageRegenerationChanges}
                    className="rounded-full bg-[var(--universe-accent)] text-[var(--universe-on-accent)] hover:bg-[var(--universe-accent)] hover:opacity-90"
                  >
                    <RefreshCw
                      className={cn("h-4 w-4", isGenerating ? "animate-spin" : null)}
                      aria-hidden="true"
                    />
                    {isGenerating ? (
                      <FormattedMessage
                        id="brandVisualUniverse.generatePendingButton"
                        defaultMessage="Generating..."
                      />
                    ) : (
                      <FormattedMessage
                        id="brandVisualUniverse.regenerateButton"
                        defaultMessage="Regenerate board"
                      />
                    )}
                  </Button>
                  <p className="text-sm text-white/50">{imageCoverageLabel}</p>
                  {renderEditAction("summary", true)}
                </div>
              </div>

              <HeroImageStack images={images} intl={intl} />
            </CardContent>
          </Card>

          {generateMutation.error ? (
            <p className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <FormattedMessage
                id="brandVisualUniverse.generateError"
                defaultMessage="Could not queue generation. Try again."
              />
            </p>
          ) : null}

          <GuidanceDeck
            eyebrow={
              <FormattedMessage
                id="brandVisualUniverse.sourceLogic.eyebrow"
                defaultMessage="Source logic"
              />
            }
            title={
              <FormattedMessage
                id="brandVisualUniverse.sourceLogic.title"
                defaultMessage="Why this universe fits"
              />
            }
            description={
              <FormattedMessage
                id="brandVisualUniverse.sourceLogic.description"
                defaultMessage="The source evidence behind this visual direction, compressed into quick creative rationale."
              />
            }
            icon={Lightbulb}
            items={universe.sourceLogic}
            tabListLabel={guidanceItemsLabel}
            action={renderEditAction("sourceLogic")}
            editingContent={renderSectionEditor("sourceLogic")}
          />

          <CreativeSection
            index="01"
            title={intl.formatMessage({
              id: "brandVisualUniverse.archetypes.title",
              defaultMessage: "Photography archetypes",
            })}
            description={intl.formatMessage({
              id: "brandVisualUniverse.archetypes.description",
              defaultMessage:
                "Repeatable photographic worlds that give image generation a recognizable point of view.",
            })}
            icon={Camera}
            action={renderEditAction("photographyArchetypes")}
          >
            {isSectionEditing("photographyArchetypes") ? (
              renderSectionEditor("photographyArchetypes")
            ) : (
              <div className="grid gap-5 xl:grid-cols-3">
                {universe.photographyArchetypes.map((archetype) => (
                  <PhotographyArchetypeCard
                    key={archetype.name}
                    archetype={archetype}
                    intl={intl}
                  />
                ))}
              </div>
            )}
          </CreativeSection>

          <div className="grid gap-6 xl:grid-cols-[0.85fr_1.15fr]">
            <CreativeSection
              index="02"
              title={intl.formatMessage({
                id: "brandVisualUniverse.environments.title",
                defaultMessage: "Environments",
              })}
              description={intl.formatMessage({
                id: "brandVisualUniverse.environments.description",
                defaultMessage: "Places and atmospheres where the brand should feel at home.",
              })}
              icon={MapPinned}
              action={renderEditAction("environments")}
            >
              {isSectionEditing("environments") ? (
                renderSectionEditor("environments")
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {universe.environments.map((environment, index) => (
                    <EnvironmentCard
                      key={environment.name}
                      environment={environment}
                      image={findImage("environment", environment.name, index)}
                      intl={intl}
                    />
                  ))}
                </div>
              )}
            </CreativeSection>

            <CreativeSection
              index="03"
              title={intl.formatMessage({
                id: "brandVisualUniverse.materials.title",
                defaultMessage: "Materials",
              })}
              description={intl.formatMessage({
                id: "brandVisualUniverse.materials.description",
                defaultMessage:
                  "Surfaces, textures, and tactile cues that should recur in visuals.",
              })}
              icon={Layers3}
              action={renderEditAction("materials")}
            >
              {isSectionEditing("materials") ? (
                renderSectionEditor("materials")
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {universe.materials.map((material, index) => (
                    <MaterialCard
                      key={material.name}
                      material={material}
                      image={findImage("material", material.name, index)}
                      intl={intl}
                    />
                  ))}
                </div>
              )}
            </CreativeSection>
          </div>

          <CreativeSection
            index="04"
            title={intl.formatMessage({
              id: "brandVisualUniverse.motifs.title",
              defaultMessage: "Motifs",
            })}
            description={intl.formatMessage({
              id: "brandVisualUniverse.motifs.description",
              defaultMessage: "Shapes, gestures, and metaphors that make the world ownable.",
            })}
            icon={Shapes}
            action={renderEditAction("motifs")}
          >
            {isSectionEditing("motifs") ? (
              renderSectionEditor("motifs")
            ) : (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {universe.motifs.map((motif, index) => (
                  <MotifCard
                    key={motif.name}
                    motif={motif}
                    image={findImage("motif", motif.name, index)}
                    intl={intl}
                  />
                ))}
              </div>
            )}
          </CreativeSection>

          <CreativeSection
            index="05"
            title={intl.formatMessage({
              id: "brandVisualUniverse.colorRoles.title",
              defaultMessage: "Color roles",
            })}
            description={intl.formatMessage({
              id: "brandVisualUniverse.colorRoles.description",
              defaultMessage: "Functional color jobs for hierarchy, emphasis, and atmosphere.",
            })}
            icon={Palette}
            action={renderEditAction("colorRoles")}
          >
            {isSectionEditing("colorRoles") ? (
              renderSectionEditor("colorRoles")
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {universe.colorRoles.map((role) => (
                  <ColorRoleCard key={role.name} role={role} intl={intl} />
                ))}
              </div>
            )}
          </CreativeSection>

          <div className="grid gap-6 xl:grid-cols-2">
            <GuidanceDeck
              eyebrow={
                <FormattedMessage
                  id="brandVisualUniverse.compositionRules.eyebrow"
                  defaultMessage="Framing"
                />
              }
              title={
                <FormattedMessage
                  id="brandVisualUniverse.compositionRules.title"
                  defaultMessage="Composition rules"
                />
              }
              description={
                <FormattedMessage
                  id="brandVisualUniverse.compositionRules.description"
                  defaultMessage="Framing rules that keep generated scenes recognisable and useful."
                />
              }
              icon={ScanLine}
              items={universe.compositionRules}
              tabListLabel={guidanceItemsLabel}
              action={renderEditAction("compositionRules")}
              editingContent={renderSectionEditor("compositionRules")}
            />
            <GuidanceDeck
              eyebrow={
                <FormattedMessage
                  id="brandVisualUniverse.antiPatterns.eyebrow"
                  defaultMessage="Avoid"
                />
              }
              title={
                <FormattedMessage
                  id="brandVisualUniverse.antiPatterns.title"
                  defaultMessage="Anti-patterns"
                />
              }
              description={
                <FormattedMessage
                  id="brandVisualUniverse.antiPatterns.description"
                  defaultMessage="Concrete visual traps Cheetah should avoid when generating or editing brand assets."
                />
              }
              icon={Ban}
              items={universe.antiPatterns}
              tabListLabel={guidanceItemsLabel}
              tone="danger"
              action={renderEditAction("antiPatterns")}
              editingContent={renderSectionEditor("antiPatterns")}
            />
          </div>

          <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/70 bg-background/95 px-5 py-3 shadow-[0_-18px_45px_rgba(44,44,42,0.08)] backdrop-blur lg:left-60 lg:px-10">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-5 text-muted-foreground">
                {hasPendingImageRegenerationChanges ? (
                  isEditing ? (
                    <FormattedMessage
                      id="brandVisualUniverse.applyChangesEditingHint"
                      defaultMessage="Update or cancel the current section before applying visual-universe changes."
                    />
                  ) : (
                    <FormattedMessage
                      id="brandVisualUniverse.applyChangesPendingHint"
                      defaultMessage="Pending changes are waiting. Apply them to save and regenerate image studies."
                    />
                  )
                ) : (
                  <FormattedMessage
                    id="brandVisualUniverse.applyChangesSavedHint"
                    defaultMessage="No visual-universe changes are waiting to be applied."
                  />
                )}
              </p>
              <Button
                type="button"
                variant="brand"
                className="w-full rounded-xl sm:w-fit"
                onClick={handleApplyChanges}
                disabled={applyChangesDisabled}
              >
                <RefreshCw
                  className={cn("h-4 w-4", applyChangesMutation.isPending ? "animate-spin" : null)}
                  aria-hidden="true"
                />
                <FormattedMessage
                  id="brandVisualUniverse.applyChangesButton"
                  defaultMessage="Apply changes"
                />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
